/**
 * MINI-FASE B.1 — Admin Audit Log (Export PII tracker).
 *
 * Vista consumer dell'endpoint `GET /api/admin/audit-log/export-pii` che elenca
 * tutti i download CSV eseguiti da admin sulla vista "Richieste Unificate".
 * Include filtri (admin_email, range date), ricerca client-side su motivazione,
 * paginazione server-side e "meta-export" CSV dei log filtrati (a sua volta
 * loggato via `POST /api/admin/audit-log/meta-export`).
 *
 * Scopo compliance: audit trail visibile al DPO / Responsabile CRM per
 * dimostrare le condizioni di ogni accesso a dati personali in chiaro.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  ShieldCheck, RefreshCw, Search, Filter, Download, ArrowLeft,
  ChevronLeft, ChevronRight, Loader2, Eye, X, Copy, CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useDebouncedValue(v, delay = 300) {
  const [dv, setDv] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setDv(v), delay);
    return () => clearTimeout(t);
  }, [v, delay]);
  return dv;
}

function ActionBadge({ action }) {
  const cfg = {
    export_richieste: { c: "bg-amber-100 text-amber-800", l: "Export Richieste" },
    audit_log_meta_export: { c: "bg-violet-100 text-violet-800", l: "Meta-export log" },
  }[action] || { c: "bg-slate-100 text-slate-700", l: action || "—" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${cfg.c}`}>
      {cfg.l}
    </span>
  );
}

function PiiBadge({ include_pii, pii_motivazione }) {
  if (include_pii) {
    return (
      <span
        title={pii_motivazione || "PII in chiaro"}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-red-100 text-red-800"
        data-testid="pii-badge-yes">
        <AlertTriangle className="w-3 h-3" /> PII CHIARO
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-emerald-100 text-emerald-800"
          data-testid="pii-badge-no">
      <ShieldCheck className="w-3 h-3" /> Mascherato
    </span>
  );
}

function DetailModal({ open, onClose, entry }) {
  const [copied, setCopied] = useState(false);
  if (!open || !entry) return null;

  const copyJson = () => {
    navigator.clipboard.writeText(JSON.stringify(entry, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={onClose} data-testid="audit-detail-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-heading text-xl text-[#0F1F14]">Dettaglio entry</h3>
            <p className="text-xs text-slate-500 mt-1 font-mono">{entry.id}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="close-detail">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
          <div>
            <div className="text-xs uppercase text-slate-500 mb-1">Azione</div>
            <ActionBadge action={entry.action} />
          </div>
          <div>
            <div className="text-xs uppercase text-slate-500 mb-1">PII</div>
            <PiiBadge include_pii={entry.include_pii} pii_motivazione={entry.pii_motivazione} />
          </div>
          <div>
            <div className="text-xs uppercase text-slate-500 mb-1">Admin</div>
            <div className="font-medium">{entry.admin_email || "—"}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-slate-500 mb-1">Timestamp</div>
            <div className="font-mono text-xs">{entry.timestamp}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-slate-500 mb-1">Righe</div>
            <div>{entry.rows_exported ?? 0}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-slate-500 mb-1">Formato</div>
            <div className="uppercase">{entry.format || "—"}</div>
          </div>
          <div className="col-span-2">
            <div className="text-xs uppercase text-slate-500 mb-1">IP · User-Agent</div>
            <div className="text-xs font-mono truncate">{entry.ip || "—"}</div>
            <div className="text-[11px] text-slate-500 truncate">{entry.user_agent || "—"}</div>
          </div>
          {entry.filename && (
            <div className="col-span-2">
              <div className="text-xs uppercase text-slate-500 mb-1">Filename</div>
              <div className="font-mono text-xs break-all">{entry.filename}</div>
            </div>
          )}
          {entry.sha256 && (
            <div className="col-span-2">
              <div className="text-xs uppercase text-slate-500 mb-1">SHA-256 file</div>
              <div className="font-mono text-[11px] break-all bg-slate-50 p-2 rounded">
                {entry.sha256}
              </div>
            </div>
          )}
          {entry.pii_motivazione && (
            <div className="col-span-2">
              <div className="text-xs uppercase text-slate-500 mb-1">Motivazione PII</div>
              <div className="bg-red-50 border border-red-200 rounded p-2 text-sm">
                {entry.pii_motivazione}
              </div>
            </div>
          )}
          <div className="col-span-2">
            <div className="text-xs uppercase text-slate-500 mb-1">Filtro applicato</div>
            <pre className="text-[11px] bg-slate-50 p-2 rounded overflow-x-auto">
              {JSON.stringify(entry.filter || {}, null, 2)}
            </pre>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={copyJson}
                   data-testid="copy-json-btn"
                   className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50">
            {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
            {copied ? "Copiato" : "Copia JSON"}
          </button>
          <button onClick={onClose}
                   className="px-4 py-2 text-sm bg-[#0F1F14] text-white rounded-lg hover:bg-[#1F3A28]">
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}

function toCsv(rows) {
  const cols = [
    "id", "timestamp", "action", "admin_email", "rows_exported",
    "include_pii", "pii_motivazione", "sha256", "format", "filename",
    "ip", "user_agent", "filter_ramo", "filter_status", "filter_partner",
    "filter_q", "filter_date_from", "filter_date_to",
  ];
  const esc = (v) => {
    if (v == null) return "";
    const s = typeof v === "object" ? JSON.stringify(v) : String(v);
    return `"${s.replace(/"/g, '""')}"`;
  };
  const header = cols.join(",");
  const lines = rows.map((r) => {
    const f = r.filter || {};
    return [
      r.id, r.timestamp, r.action, r.admin_email, r.rows_exported,
      r.include_pii ? "true" : "false", r.pii_motivazione, r.sha256,
      r.format, r.filename, r.ip, r.user_agent,
      f.ramo, f.status, f.partner, f.q, f.date_from, f.date_to,
    ].map(esc).join(",");
  });
  return [header, ...lines].join("\n");
}

async function sha256Hex(text) {
  const buf = new TextEncoder().encode(text);
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0")).join("");
}

export default function AdminAuditLogPage() {
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [detail, setDetail] = useState(null);

  const filters = useMemo(() => ({
    admin_email: sp.get("admin_email") || "",
    date_from: sp.get("date_from") || "",
    date_to: sp.get("date_to") || "",
    q: sp.get("q") || "",
    limit: parseInt(sp.get("limit") || "50", 10),
    offset: parseInt(sp.get("offset") || "0", 10),
  }), [sp]);
  const qDebounced = useDebouncedValue(filters.q, 300);

  const setFilter = useCallback((key, value) => {
    const next = new URLSearchParams(sp);
    if (value === "" || value == null) next.delete(key);
    else next.set(key, String(value));
    if (key !== "offset" && key !== "limit") next.set("offset", "0");
    setSp(next);
  }, [sp, setSp]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.admin_email) params.set("admin_email", filters.admin_email);
      if (filters.date_from) params.set("date_from", filters.date_from);
      if (filters.date_to) params.set("date_to", filters.date_to);
      params.set("limit", String(filters.limit));
      params.set("offset", String(filters.offset));
      const r = await axios.get(`${API}/admin/audit-log/export-pii?${params.toString()}`,
                                    { withCredentials: true });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      console.error(e);
      toast.error("Errore caricamento audit log");
    } finally {
      setLoading(false);
    }
  }, [filters.admin_email, filters.date_from, filters.date_to,
      filters.limit, filters.offset]);

  useEffect(() => { load(); }, [load]);

  // Ricerca client-side su motivazione + admin_email
  const filtered = useMemo(() => {
    if (!qDebounced) return items;
    const needle = qDebounced.toLowerCase();
    return items.filter((r) => {
      const hay = [
        r.admin_email, r.pii_motivazione, r.filename, r.sha256,
        r.filter?.ramo, r.filter?.status, r.filter?.partner, r.filter?.q,
      ].filter(Boolean).map((v) => String(v).toLowerCase()).join("|");
      return hay.includes(needle);
    });
  }, [items, qDebounced]);

  const doMetaExport = async () => {
    if (filtered.length === 0) {
      toast.error("Nessun record da esportare");
      return;
    }
    setExporting(true);
    try {
      const csv = toCsv(filtered);
      const sha = await sha256Hex(csv);
      // Registra il meta-export a backend
      try {
        await axios.post(`${API}/admin/audit-log/meta-export`, {
          admin_email: filters.admin_email || null,
          date_from: filters.date_from || null,
          date_to: filters.date_to || null,
          rows_exported: filtered.length,
          sha256_file: sha,
          format: "csv",
        }, { withCredentials: true });
      } catch (e) {
        console.warn("meta-export log fail (non blocca download):", e);
      }
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit_export_pii_${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Esportate ${filtered.length} righe (SHA256: ${sha.slice(0, 12)}…)`);
      // Ricarica per mostrare la nuova entry meta_export
      setTimeout(load, 400);
    } catch (e) {
      console.error(e);
      toast.error("Errore export CSV");
    } finally {
      setExporting(false);
    }
  };

  const resetFilters = () => {
    const next = new URLSearchParams();
    next.set("limit", "50");
    next.set("offset", "0");
    setSp(next);
  };

  const totPages = Math.max(1, Math.ceil(total / filters.limit));
  const currentPage = Math.floor(filters.offset / filters.limit) + 1;

  return (
    <div className="p-6 max-w-full" data-testid="admin-audit-log-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Link to="/admin/dashboard"
                   className="text-sm text-slate-500 hover:text-[#0F1F14] inline-flex items-center gap-1"
                   data-testid="back-to-dashboard">
              <ArrowLeft className="w-4 h-4" /> Dashboard
            </Link>
            <span className="text-slate-300">·</span>
            <Link to="/admin/cockpit"
                   className="text-sm text-slate-500 hover:text-[#0F1F14]">
              Richieste
            </Link>
          </div>
          <h1 className="font-heading text-3xl text-[#0F1F14] flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-[#B9A268]" />
            Audit Log · Export PII
          </h1>
          <p className="text-sm text-[#4A5E51] mt-1">
            Tracciamento accessi a dati personali in chiaro · {total} entry totali
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={doMetaExport}
                   disabled={exporting || filtered.length === 0}
                   data-testid="meta-export-btn"
                   className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#0F1F14] text-white rounded-lg hover:bg-[#1F3A28] disabled:opacity-50">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Esporta log ({filtered.length})
          </button>
          <button onClick={load}
                   data-testid="refresh-btn"
                   className="p-2 rounded-lg hover:bg-slate-100">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Filtri */}
      <div className="bg-white border border-[#E5E2DB] rounded-2xl p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="lg:col-span-2">
            <label className="text-[11px] uppercase text-slate-500 font-medium">Ricerca</label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                data-testid="audit-search-input"
                placeholder="Motivazione, SHA256, filtro applicato…"
                value={filters.q}
                onChange={(e) => setFilter("q", e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg" />
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase text-slate-500 font-medium">Admin</label>
            <input
              data-testid="audit-filter-admin-email"
              placeholder="admin@…"
              value={filters.admin_email}
              onChange={(e) => setFilter("admin_email", e.target.value)}
              className="mt-1 w-full px-3 py-2 text-sm border rounded-lg" />
          </div>
          <div>
            <label className="text-[11px] uppercase text-slate-500 font-medium">Da</label>
            <input type="date" value={filters.date_from}
                    data-testid="audit-filter-date-from"
                    onChange={(e) => setFilter("date_from", e.target.value)}
                    className="mt-1 w-full px-3 py-2 text-sm border rounded-lg" />
          </div>
          <div>
            <label className="text-[11px] uppercase text-slate-500 font-medium">A</label>
            <input type="date" value={filters.date_to}
                    data-testid="audit-filter-date-to"
                    onChange={(e) => setFilter("date_to", e.target.value)}
                    className="mt-1 w-full px-3 py-2 text-sm border rounded-lg" />
          </div>
        </div>
        {(filters.admin_email || filters.date_from || filters.date_to || filters.q) && (
          <div className="mt-3 flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <span className="text-xs text-slate-500">Filtri attivi</span>
            <button onClick={resetFilters}
                     data-testid="reset-filters-btn"
                     className="text-xs text-red-600 hover:underline ml-2">
              Azzera
            </button>
          </div>
        )}
      </div>

      {/* Tabella */}
      <div className="bg-white border border-[#E5E2DB] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F7F5EE] text-xs uppercase text-slate-600">
              <tr>
                <th className="px-4 py-3 text-left">Timestamp</th>
                <th className="px-4 py-3 text-left">Azione</th>
                <th className="px-4 py-3 text-left">Admin</th>
                <th className="px-4 py-3 text-left">PII</th>
                <th className="px-4 py-3 text-right">Righe</th>
                <th className="px-4 py-3 text-left">Motivazione / Filtro</th>
                <th className="px-4 py-3 text-left">SHA256</th>
                <th className="px-4 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-slate-500">
                    <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
                    Caricamento…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-slate-500"
                       data-testid="empty-state">
                    Nessuna entry corrispondente ai filtri.
                  </td>
                </tr>
              )}
              {!loading && filtered.map((r) => {
                const filterSummary = r.filter ? [
                  r.filter.ramo && `Ramo:${r.filter.ramo}`,
                  r.filter.status && `Status:${r.filter.status}`,
                  r.filter.partner && `Partner:${r.filter.partner}`,
                  r.filter.q && `q:"${r.filter.q}"`,
                  r.filter.date_from && `≥${r.filter.date_from}`,
                  r.filter.date_to && `≤${r.filter.date_to}`,
                ].filter(Boolean).join(" · ") : "";
                return (
                  <tr key={r.id} className="border-t border-[#E5E2DB] hover:bg-[#FAF9F4]"
                       data-testid={`audit-row-${r.id}`}>
                    <td className="px-4 py-2 font-mono text-[11px] whitespace-nowrap">
                      {(r.timestamp || "").replace("T", " ").slice(0, 19)}
                    </td>
                    <td className="px-4 py-2"><ActionBadge action={r.action} /></td>
                    <td className="px-4 py-2 max-w-[180px] truncate" title={r.admin_email}>
                      {r.admin_email || "—"}
                    </td>
                    <td className="px-4 py-2">
                      <PiiBadge include_pii={r.include_pii} pii_motivazione={r.pii_motivazione} />
                    </td>
                    <td className="px-4 py-2 text-right font-medium">{r.rows_exported ?? 0}</td>
                    <td className="px-4 py-2 max-w-[280px]">
                      {r.pii_motivazione && (
                        <div className="text-xs text-red-700 truncate italic"
                              title={r.pii_motivazione}>
                          {r.pii_motivazione}
                        </div>
                      )}
                      {filterSummary && (
                        <div className="text-[11px] text-slate-500 truncate"
                              title={filterSummary}>
                          {filterSummary}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 font-mono text-[10px] text-slate-500">
                      {r.sha256 ? r.sha256.slice(0, 12) + "…" : "—"}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => setDetail(r)}
                               data-testid={`view-audit-${r.id}`}
                               className="p-1.5 hover:bg-slate-100 rounded"
                               title="Dettaglio">
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Paginazione */}
        {total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#E5E2DB] bg-[#F7F5EE]/40 text-sm">
            <div className="text-slate-600">
              Pagina <strong>{currentPage}</strong> di <strong>{totPages}</strong> · {total} entry
              {qDebounced && ` · ${filtered.length} match locali`}
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="page-prev"
                onClick={() => setFilter("offset", Math.max(0, filters.offset - filters.limit))}
                disabled={filters.offset === 0}
                className="p-2 rounded hover:bg-slate-200 disabled:opacity-40">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                data-testid="page-next"
                onClick={() => setFilter("offset", filters.offset + filters.limit)}
                disabled={filters.offset + filters.limit >= total}
                className="p-2 rounded hover:bg-slate-200 disabled:opacity-40">
                <ChevronRight className="w-4 h-4" />
              </button>
              <select value={filters.limit}
                       data-testid="page-size"
                       onChange={(e) => setFilter("limit", parseInt(e.target.value, 10))}
                       className="text-xs border rounded px-2 py-1">
                {[25, 50, 100, 200].map((n) => (
                  <option key={n} value={n}>{n} / pag</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      <DetailModal open={!!detail} onClose={() => setDetail(null)} entry={detail} />
    </div>
  );
}
